import type { Language } from "@/constants";
import type { TranslationF } from "./type-utils";

export function getTimeSinceText(
	timestamp: Date | string,
	t: TranslationF,
	language: Language,
): string {
	let ts: Date;

	if (typeof timestamp === "string") {
		ts = new Date(timestamp);
	} else {
		ts = timestamp;
	}

	const now = new Date();

	const isSameDay = (d1: Date, d2: Date): boolean =>
		d1.getFullYear() === d2.getFullYear() &&
		d1.getMonth() === d2.getMonth() &&
		d1.getDate() === d2.getDate();

	const getStartOfDay = (d: Date): Date =>
		new Date(d.getFullYear(), d.getMonth(), d.getDate());

	const startNow = getStartOfDay(now);
	const startTs = getStartOfDay(ts);

	const diffInMilliseconds = startNow.getTime() - startTs.getTime();
	const diffInDays = Math.round(diffInMilliseconds / (1000 * 60 * 60 * 24));

	if (isSameDay(now, ts)) {
		return t("time_since.today", {
			time: ts.toLocaleTimeString(language, {
				hour: "numeric",
				minute: "2-digit",
			}),
		});
	}

	if (diffInDays === 1) {
		return t("time_since.yesterday", {
			time: ts.toLocaleTimeString(language, {
				hour: "numeric",
				minute: "2-digit",
			}),
		});
	}

	if (diffInDays === -1) {
		return t("time_since.tomorrow", {
			time: ts.toLocaleTimeString(language, {
				hour: "numeric",
				minute: "2-digit",
			}),
		});
	}

	if (diffInDays < 7 && diffInDays > -7) {
		return t("time_since.days_ago", {
			weekday: ts.toLocaleDateString(language, { weekday: "long" }),
			time: ts.toLocaleTimeString(language, {
				hour: "numeric",
				minute: "2-digit",
			}),
		});
	}

	return t("time_since.weeks_ago", {
		date: ts.toLocaleDateString(language, {
			month: "short",
			day: "numeric",
		}),
		time: ts.toLocaleTimeString(language, {
			hour: "numeric",
			minute: "2-digit",
		}),
	});
}
